import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo.webp";
import DevAvatarAnimation from "@/components/auth/DevAvatarAnimation";

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token");

  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link.");
      setLoading(false);
      return;
    }
    supabase
      .rpc("lookup_invitation_by_token", { _token: token })
      .then(({ data, error: err }) => {
        const inv = data?.[0];
        if (err || !inv) {
          setError("This invitation is invalid or has already been used.");
        } else if (new Date(inv.expires_at) < new Date()) {
          setError("This invitation has expired. Please contact the administrator.");
        } else {
          setInvitation(inv);
        }
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: { full_name: invitation.full_name },
          emailRedirectTo: window.location.origin,
        },
      });
      if (signUpError && !/already registered/i.test(signUpError.message)) throw signUpError;

      // Sign in to obtain a JWT so the secure accept-invitation function can verify the caller
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      });
      if (signInError) {
        // Email may require confirmation before sign-in; admin will assign the role manually
        console.warn("Could not sign in to assign role automatically:", signInError.message);
      } else {
        const { error: fnError } = await supabase.functions.invoke("accept-invitation", {
          body: { token },
        });
        if (fnError) console.warn("Role assignment via function failed, will be handled by admin:", fnError);
      }

      toast({
        title: "Account activated!",
        description: "Please check your email to verify your account, then sign in.",
      });
      navigate("/login");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 gap-6">
      {/* Header: Logo */}
      <div className="flex flex-col items-center gap-2">
        <div className="h-[104px] w-[104px] rounded-full bg-primary/10 flex items-center justify-center p-2">
          <img src={logo} alt="St. David College" className="h-full w-full object-contain" />
        </div>
        <h1 className="text-xl font-bold text-foreground text-center">St. David College of Health Sciences</h1>
        <p className="text-sm font-medium text-muted-foreground">Staff Invitation Portal</p>
      </div>

      {/* Invitation Card */}
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-lg">Activate Your Account</CardTitle>
          <CardDescription>Set up your password to get started</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center space-y-4">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to Login</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={invitation.full_name} disabled />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={invitation.email} disabled />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Input value={invitation.role.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())} disabled />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Activating..." : "Activate Account"}
              </Button>
            </form>
          )}
          <div className="flex justify-center pt-4">
            <DevAvatarAnimation />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
